/**
 * AreaChartCard Component
 *
 * Reusable Area Chart with Korat brand colors and responsive design
 * Perfect for showing volume/magnitude trends over time
 */

import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'

// Korat brand colors
const COLORS = {
  navy: {
    stroke: '#1e3a5f',
    fill: '#1e3a5f',
    fillOpacity: 0.6,
  },
  blue: {
    stroke: '#2563eb',
    fill: '#3b82f6',
    fillOpacity: 0.5,
  },
  orange: {
    stroke: '#f97316',
    fill: '#fb923c',
    fillOpacity: 0.5,
  },
  green: {
    stroke: '#10b981',
    fill: '#34d399',
    fillOpacity: 0.5,
  },
  purple: {
    stroke: '#8b5cf6',
    fill: '#a78bfa',
    fillOpacity: 0.5,
  },
}

export interface AreaData {
  [key: string]: string | number
}

export interface AreaConfig {
  dataKey: string
  name: string
  color: keyof typeof COLORS
  stackId?: string
}

export interface AreaChartCardProps {
  title: string
  description?: string
  data: AreaData[]
  areas: AreaConfig[]
  xAxisKey: string
  xAxisLabel?: string
  yAxisLabel?: string
  height?: number
  showGrid?: boolean
  showLegend?: boolean
  stacked?: boolean
  isLoading?: boolean
}

export function AreaChartCard({
  title,
  description,
  data,
  areas,
  xAxisKey,
  xAxisLabel,
  yAxisLabel,
  height = 350,
  showGrid = true,
  showLegend = true,
  stacked = false,
  isLoading = false,
}: AreaChartCardProps) {
  if (isLoading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <div className="h-6 bg-slate-200 rounded w-1/3 mb-2 animate-pulse" />
        {description && <div className="h-4 bg-slate-100 rounded w-2/3 mb-4 animate-pulse" />}
        <div className="bg-slate-200 rounded animate-pulse" style={{ height }} />
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
        <h3 className="text-base font-semibold text-korat-navy-700">{title}</h3>
        {description && <p className="text-sm text-slate-500 mt-1">{description}</p>}
        <div className="flex items-center justify-center text-slate-400 text-sm" style={{ height }}>
          ไม่มีข้อมูล
        </div>
      </div>
    )
  }

  // Format large numbers for Y-axis (Thai style)
  const formatYAxis = (value: number) => {
    if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)} พ.ล.`
    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)} ล้าน`
    if (value >= 100_000) return `${(value / 100_000).toFixed(1)} แสน`
    if (value >= 10_000) return `${(value / 10_000).toFixed(1)} หมื่น`
    if (value >= 1_000) return `${(value / 1_000).toFixed(1)} พัน`
    return new Intl.NumberFormat('th-TH').format(value)
  }

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null

    return (
      <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3">
        <p className="text-sm font-semibold text-korat-navy-700 mb-2">{label}</p>
        {payload.map((entry: any, index: number) => (
          <div key={index} className="flex items-center gap-2 text-xs">
            <div
              className="w-3 h-3 rounded-sm"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-slate-600">{entry.name}:</span>
            <span className="font-semibold tabular-nums">
              {new Intl.NumberFormat('th-TH').format(entry.value)}
            </span>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-shadow p-6">
      {/* Header */}
      <div className="mb-4">
        <h3 className="text-base font-semibold text-korat-navy-700">{title}</h3>
        {description && <p className="text-sm text-slate-500 mt-1 leading-relaxed">{description}</p>}
      </div>

      {/* Chart */}
      <ResponsiveContainer width="100%" height={height}>
        <AreaChart data={data} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />}

          <XAxis
            dataKey={xAxisKey}
            tick={{ fontSize: 13, fill: '#334155', fontWeight: 500 }}
            stroke="#94a3b8"
            label={
              xAxisLabel
                ? { value: xAxisLabel, position: 'insideBottom', offset: -5, style: { fontSize: 13 } }
                : undefined
            }
          />

          <YAxis
            tickFormatter={formatYAxis}
            tick={{ fontSize: 13, fill: '#64748b', fontWeight: 500 }}
            stroke="#94a3b8"
            label={
              yAxisLabel
                ? { value: yAxisLabel, angle: -90, position: 'insideLeft', style: { fontSize: 13 } }
                : undefined
            }
          />

          <Tooltip content={<CustomTooltip />} />

          {showLegend && (
            <Legend
              wrapperStyle={{ fontSize: 13, fontWeight: 500 }}
              iconType="rect"
            />
          )}

          {areas.map((area, index) => (
            <Area
              key={index}
              type="monotone"
              dataKey={area.dataKey}
              name={area.name}
              stroke={COLORS[area.color].stroke}
              fill={COLORS[area.color].fill}
              fillOpacity={COLORS[area.color].fillOpacity}
              stackId={stacked ? (area.stackId || 'stack') : undefined}
              strokeWidth={2}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}
